import React, { useEffect, useState } from "react";
import "./OrderSummary.css";
import { useCart } from "../context/CartContext";
import {
  trackCartOrderDone,
  trackCartSummaryEditClicked,
  trackCartSummaryViewed,
  trackCartConversionCompleted,
} from "../utils/analytics";

const formatTime = () => {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  const day = now.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${day} ${months[now.getMonth()]}, ${h12}:${m} ${ampm}`;
};

const NEW_TAG_ID = "6932636dec4624b7afca0266";

// Dietary indicators (veg / non-veg / egg / spicy) for a cart item.
const renderDietaryTags = (item) => {
  const tags = Array.isArray(item.tags) ? item.tags : [];
  if (tags.length === 0) return null;
  const hasEgg = tags.some((t) => t?.name?.toLowerCase().includes("egg"));
  const hasNonVeg = tags.some((t) => {
    const n = t?.name?.toLowerCase() || "";
    return n.includes("non") && n.includes("veg");
  });
  const icons = [];
  if (hasEgg) {
    icons.push(<img key="egg" src="/EggTag.png" alt="Egg" className="order-summary-diet-egg" />);
  }
  tags.forEach((t, i) => {
    const name = t?.name?.toLowerCase() || "";
    if (t?.id === NEW_TAG_ID || name.includes("new")) return;
    if (hasEgg && name.includes("egg")) return;
    if (hasEgg && hasNonVeg && name.includes("non")) return;
    if (t?.tagIcon?.fullPath) {
      icons.push(
        <img key={t.id || i} src={t.tagIcon.fullPath} alt={t.name || ""} className="order-summary-diet-icon" />
      );
    }
  });
  if (icons.length === 0) return null;
  return <span className="order-summary-diet-tags">{icons}</span>;
};

function ItemThumb({ src, quantity }) {
  const [err, setErr] = useState(false);
  return (
    <div className="order-summary-thumb">
      {src && !err ? (
        <img src={src} alt="" className="order-summary-thumb-img" onError={() => setErr(true)} />
      ) : (
        <div className="order-summary-thumb-ph" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M4 8h13a3 3 0 0 1 0 6h-1M4 8v6a5 5 0 0 0 5 5h3a5 5 0 0 0 5-5V8H4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
            <path d="M8 2.5c-.5.6-.5 1.4 0 2M11.5 2.5c-.5.6-.5 1.4 0 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </div>
      )}
      <span className="order-summary-thumb-qty">{quantity}</span>
    </div>
  );
}

export default function OrderSummary({ cafeId, cafeName, onEditOrder, onOrderDone }) {
  const { items, itemCount, subtotal, taxTotal, grandTotal, phoneNumber, tableNumber } = useCart();
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    if (itemCount === 0 && !completed) {
      onOrderDone();
    }
  }, [itemCount, completed, onOrderDone]);

  useEffect(() => {
    trackCartSummaryViewed({
      cafeId,
      cafeName,
      items,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    });
  }, [cafeId, cafeName, items, itemCount, subtotal, taxTotal, grandTotal, tableNumber, phoneNumber]);

  const handleOrderDone = () => {
    const commonParams = {
      cafeId,
      cafeName,
      items,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    };
    trackCartOrderDone(commonParams);
    trackCartConversionCompleted(commonParams);
    setCompleted(true);
    setTimeout(() => {
      onOrderDone();
    }, 1600);
  };

  if (completed) {
    return (
      <div className="order-summary order-summary-done">
        <div className="order-done-content">
          <div className="order-done-check">
            <svg viewBox="0 0 52 52" className="order-done-svg">
              <circle className="order-done-circle" cx="26" cy="26" r="24" />
              <path className="order-done-tick" d="M14 27l7 7 16-16" />
            </svg>
          </div>
          <h2 className="order-done-title">Order Complete</h2>
          <p className="order-done-subtitle">Cart cleared — ready for next customer</p>
        </div>
      </div>
    );
  }

  return (
    <div className="order-summary">
      <div className="order-summary-hero">
        <img
          src="/BTCArtSmall.png"
          alt=""
          className="order-summary-art"
          aria-hidden="true"
        />
        <div className="order-summary-hero-content">
          <div className="order-summary-hero-top">
            <div className="order-summary-logo-wrap">
              <img src="/BTC_Blue_Paradise.png" alt="Blue Tokai" className="order-summary-logo" />
            </div>
            {tableNumber && (
              <div className="order-summary-table-badge">
                <span className="order-summary-table-label">Table</span>
                <span className="order-summary-table-num">#{tableNumber}</span>
              </div>
            )}
          </div>
          <h1 className="order-summary-heading">Order Summary</h1>
          <div className="order-summary-meta-line">
            {cafeName && <span>{cafeName}</span>}
            <span className="order-summary-meta-dot">·</span>
            <span>{formatTime()}</span>
            <span className="order-summary-meta-dot">·</span>
            <span>{itemCount} item{itemCount === 1 ? "" : "s"}</span>
          </div>
          {phoneNumber && (
            <div className="order-summary-hero-phone">
              <span className="order-summary-hero-phone-label">Mobile</span>
              <span className="order-summary-hero-phone-value">+91 {phoneNumber}</span>
            </div>
          )}
        </div>
      </div>

      <div className="order-summary-barista-pill-wrap">
        <div className="order-summary-barista-pill">
          <span className="order-summary-barista-dot" />
          <span className="order-summary-barista-text">Show this to your barista</span>
        </div>
      </div>

      <div className="order-summary-body">
        <div className="order-summary-body-inner">
          <div className="order-summary-items">
            {items.map((item) => (
              <div className="order-summary-row" key={item.id}>
                <ItemThumb src={item.imageUrl} quantity={item.quantity} />
                <div className="order-summary-item-info">
                  <p className="order-summary-item-name">
                    {renderDietaryTags(item)}
                    <span className="order-summary-item-name-text">{item.name}</span>
                  </p>
                  {(item.variantTitle || item.selectedOptions?.length > 0) && (
                    <p className="order-summary-item-options">
                      {[
                        item.variantTitle,
                        ...(item.selectedOptions || []).map((o) =>
                          Number(o?.price) > 0 ? `${o.title} (+₹${Math.round(o.price)})` : o.title
                        ),
                      ].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <span className="order-summary-item-price">₹{Math.round(item.unitPrice * item.quantity)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="order-summary-footer">
        <div className="order-summary-totals">
          <div className="order-summary-totals-row">
            <span>{itemCount} item{itemCount === 1 ? "" : "s"}</span>
            <span>₹{Math.round(subtotal)}</span>
          </div>
          {taxTotal > 0 && (
            <div className="order-summary-totals-row">
              <span>Taxes</span>
              <span>₹{Math.round(taxTotal)}</span>
            </div>
          )}
          <div className="order-summary-totals-grand">
            <span>Total</span>
            <strong>₹{Math.round(grandTotal)}</strong>
          </div>
        </div>
        <div className="order-summary-pay-badge">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 4.5v4M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Pay after order is served
        </div>
        {!confirmingClear ? (
          <div className="order-summary-actions">
            <button
              className="order-summary-done-btn"
              onClick={() => {
                trackCartSummaryEditClicked({
                  cafeId,
                  cafeName,
                  itemCount,
                  lineCount: items.length,
                  subtotal,
                  taxTotal,
                  grandTotal,
                  hasTableNumber: Boolean(tableNumber),
                  hasPhoneNumber: Boolean(phoneNumber),
                });
                onEditOrder();
              }}
            >
              ← Edit Order
            </button>
            <button className="order-summary-edit-btn" onClick={() => setConfirmingClear(true)}>
              Clear Cart
            </button>
          </div>
        ) : (
          <div className="order-summary-confirm">
            <p className="order-summary-confirm-text">Clear cart and start fresh?</p>
            <div className="order-summary-confirm-actions">
              <button className="order-summary-confirm-yes" onClick={handleOrderDone}>
                Yes, Done
              </button>
              <button className="order-summary-confirm-no" onClick={() => setConfirmingClear(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
